import React from "react";
import { Fire, TrendUp } from "@phosphor-icons/react";
import { StatCard } from "@/_self_managed/SM_Dashboard/comps/Stats";
import { StatCardSkeleton } from "@/_self_managed/SM_Dashboard/comps/DashboardStatsBar";
import { SignalCategoriesProvider, useSignalCategories } from "@/components/analysisModules/TopicClusteringTreeView/SignalCategoriesContext";
import { resolveZones } from "@/components/analysisModules/TopicClusteringTreeView/PrioritiesLayout/zoneStyles";
import { usePrioritiesDashboardScope } from "./usePrioritiesDashboardScope";
import { isSinglePeriod, usePrioritiesScoring } from "./usePrioritiesScoring";
import { SM_UIContent } from "@/_self_managed/SM_UIContent";

/**
 * Two Priorities readings for the Dashboard's stats bar, in the bar's own
 * card: how many sub-topics sit in fires (tinted with the zone's colour),
 * and the corpus's observation total with how many the selected period
 * added. Scored over the same scope as the rest of the New tab
 * (usePrioritiesDashboardScope), so the requests are shared.
 */
const FIRES_COPY = SM_UIContent.SM_Dashboard.firesStat;
const FIRES_SINGLE_PERIOD_READING = SM_UIContent.SM_Dashboard.singlePeriod.fires;
const NO_DATA_READING = "No observations yet";


export const PrioritiesStatCards = () => {
  const scope = usePrioritiesDashboardScope();
  return (
    <SignalCategoriesProvider value={scope.theme}>
      <Cards scope={scope} />
    </SignalCategoriesProvider>
  );
};

const Cards = ({ scope }) => {
  const categoryTheme = useSignalCategories();
  const { counts, scored, loading } = usePrioritiesScoring({
    topicClusteringLens: scope.topicClusteringLens,
    historyFilters: scope.historyFilters,
    period: scope.period,
    onCountsMeta: scope.onCountsMeta,
    compareKey: scope.compareKey,
  });
  const zones = resolveZones(categoryTheme.prioritiesCopy);
  if (scope.periodLoading || (scope.period && loading)) {
    return (
      <>
        <StatCardSkeleton />
        <StatCardSkeleton />
      </>
    );
  }
  // No dated observation on the tenant (nothing annotated yet): the readings
  // are zero, not pending.
  if (!scope.period) {
    return (
      <>
        <StatCard iconComp={Fire} title={zones.fires.label} value="0" subline={NO_DATA_READING} />
        <StatCard iconComp={TrendUp} title="Observations" value="0" subline={NO_DATA_READING} />
      </>
    );
  }
  const { priorities, meta } = scored;
  const fires = priorities.filter((item) => item.zone === "fires").length;
  // One month of data and nothing before it: fires need momentum, which
  // needs an earlier month, so the count is not zero but unknowable — the
  // same flag that blurs the section's time-wise widgets.
  const singlePeriod = isSinglePeriod(meta);
  const now = meta.totalNow || 0;
  return (
    <>
      <StatCard
        iconComp={Fire}
        title={zones.fires.label}
        value={singlePeriod ? "–" : String(fires)}
        // One period of data: fires can't be known yet. None: say so,
        // plainly. Some: the zone's reading in its colour.
        subline={singlePeriod ? FIRES_SINGLE_PERIOD_READING : fires > 0 ? FIRES_COPY.some : FIRES_COPY.none}
        sublineColor={singlePeriod || fires > 0 ? zones.fires.text : undefined}
        bg={zones.fires.field}
      />
      {/* The whole corpus's observations, and how many of them the period
          added — the same reading the Documents card gives for documents. */}
      <StatCard
        iconComp={TrendUp}
        title="Observations"
        value={(counts?.scope?.annotationCount ?? 0).toLocaleString()}
        subline={`+${now.toLocaleString()} in ${meta.nowLabel}`}
      />
    </>
  );
};
